import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Sun,
  CloudSun,
  Cloud,
  CloudFog,
  CloudDrizzle,
  CloudRain,
  CloudLightning,
  Wind,
  Waves,
  Thermometer,
} from "lucide-react";
import { useLanguage } from "@/lib/i18n";

// Consulte diretamente as APIs públicas: o clima não depende do backend do site.
const LATITUDE = -23.4914;
const LONGITUDE = -45.1289;
const LOCATION = `latitude=${LATITUDE}&longitude=${LONGITUDE}`;
const WEATHER_URL = `https://api.open-meteo.com/v1/forecast?${LOCATION}&current=temperature_2m,weather_code,wind_speed_10m&wind_speed_unit=kmh&timezone=America%2FSao_Paulo`;
const MARINE_URL = `https://marine-api.open-meteo.com/v1/marine?${LOCATION}&current=wave_height,sea_surface_temperature&timezone=America%2FSao_Paulo&cell_selection=sea`;
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

async function fetchWeather() {
  const [forecast, marine] = await Promise.allSettled([
    axios.get(WEATHER_URL, { timeout: 10000 }),
    axios.get(MARINE_URL, { timeout: 10000 }),
  ]);

  const current = forecast.status === "fulfilled" ? forecast.value.data?.current : null;
  if (current && isNumber(current.temperature_2m) && isNumber(current.weather_code)) {
    const sea = marine.status === "fulfilled" ? marine.value.data?.current : null;
    return {
      temp: current.temperature_2m,
      weather_code: current.weather_code,
      wind_kmh: isNumber(current.wind_speed_10m) ? current.wind_speed_10m : null,
      wave_m: isNumber(sea?.wave_height) ? sea.wave_height : null,
      sea_temp: isNumber(sea?.sea_surface_temperature) ? sea.sea_surface_temperature : null,
    };
  }

  // Fallback opcional para instalações em que o backend já está configurado.
  if (/^https?:\/\//i.test(BACKEND_URL)) {
    const response = await axios.get(`${BACKEND_URL}/api/weather`, { timeout: 8000 });
    const data = response.data;
    if (isNumber(data?.temp) && isNumber(data?.weather_code)) {
      return {
        temp: data.temp,
        weather_code: data.weather_code,
        wind_kmh: isNumber(data.wind_kmh) ? data.wind_kmh : null,
        wave_m: isNumber(data.wave_m) ? data.wave_m : null,
        sea_temp: isNumber(data.sea_temp) ? data.sea_temp : null,
      };
    }
  }

  throw new Error("Não foi possível consultar o clima em tempo real.");
}
const EASE = [0.22, 1, 0.36, 1];

const conditionFor = (code) => {
  if (code === 0) return { key: "clear", Icon: Sun };
  if (code <= 2) return { key: "partly", Icon: CloudSun };
  if (code === 3) return { key: "cloudy", Icon: Cloud };
  if (code <= 48) return { key: "fog", Icon: CloudFog };
  if (code <= 57) return { key: "drizzle", Icon: CloudDrizzle };
  if (code <= 82) return { key: "rain", Icon: CloudRain };
  return { key: "storm", Icon: CloudLightning };
};

const seaFor = (m) => (m < 0.5 ? "calm" : m < 1.25 ? "moderate" : "rough");

export default function WeatherWidget() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let loading = false;

    const updateWeather = async () => {
      if (loading) return;
      loading = true;
      try {
        const next = await fetchWeather();
        if (alive) {
          setData(next);
          setFailed(false);
        }
      } catch (error) {
        if (alive) {
          setData(null);
          setFailed(true);
        }
      } finally {
        loading = false;
      }
    };

    updateWeather();
    // Atualiza o clima automaticamente enquanto o site permanecer aberto.
    const timer = setInterval(updateWeather, 10 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const cond = data ? conditionFor(data.weather_code) : null;
  const sea = data && data.wave_m != null ? seaFor(data.wave_m) : null;

  return (
    <motion.div
      data-testid="weather-widget"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: EASE }}
      className="mb-10 overflow-hidden rounded-[2rem] border border-[#3D2B1F]/10 bg-[#082F38] p-6 sm:p-8"
    >
      <div className="flex flex-wrap items-center gap-x-8 gap-y-5">
        <div className="flex items-center gap-4">
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#E9C46A]/15 text-[#E9C46A]">
            {cond ? <cond.Icon size={28} /> : <Sun size={28} className="animate-pulse" />}
          </div>
          <div>
            <p className="font-jetmono text-[10px] uppercase tracking-[0.22em] text-[#F4EBD9]/60">
              {t.weather.title}
            </p>
            {data ? (
              <p className="mt-1 font-cabinet text-2xl font-extrabold tracking-tight text-[#FAF6EE]">
                {Math.round(data.temp)}°C
                <span className="ml-3 align-middle font-playfair text-base font-normal italic text-[#E9C46A]">
                  {t.weather.conditions[cond.key]}
                </span>
              </p>
            ) : (
              <p className="mt-1 font-cabinet text-lg font-bold text-[#FAF6EE]/70">
                {failed ? t.weather.unavailable : t.weather.loading}
              </p>
            )}
          </div>
        </div>

        {data && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="weather-details">
            {data.wind_kmh != null && (
              <span className="inline-flex items-center gap-2 text-sm text-[#F4EBD9]/80">
                <Wind size={16} className="text-[#2A9D8F]" />
                {t.weather.wind} {Math.round(data.wind_kmh)} km/h
              </span>
            )}
            {data.wave_m != null && (
              <span className="inline-flex items-center gap-2 text-sm text-[#F4EBD9]/80">
                <Waves size={16} className="text-[#2A9D8F]" />
                {t.weather.waves} {data.wave_m.toFixed(1)} m
              </span>
            )}
            {data.sea_temp != null && (
              <span className="inline-flex items-center gap-2 text-sm text-[#F4EBD9]/80">
                <Thermometer size={16} className="text-[#2A9D8F]" />
                {t.weather.sea} {Math.round(data.sea_temp)}°C
              </span>
            )}
            {sea && (
              <span className="rounded-full bg-[#2A9D8F]/20 px-4 py-1.5 font-jetmono text-[10px] uppercase tracking-[0.18em] text-[#7FD8CC]">
                {t.weather.seaState[sea]}
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
